/**
 * AdminB2BPlaquette.jsx — Gestion de la plaquette B2B.
 *
 * Vue admin dédiée à :
 *   - Prévisualiser la plaquette (iframe sur le vault slug)
 *   - Créer / révoquer des tokens d'accès partenaire
 *   - Générer un QR code à envoyer aux prospects (lien pré-authentifié)
 *   - Copier le lien de partage, télécharger le QR en PNG
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  QrCode, Plus, Copy, Trash2, ExternalLink, Download, Eye, EyeOff,
  Loader2, Share2, ShieldCheck, Clock, Users as UsersIcon, Mail,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import api, { API_URL } from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};
const fmtDateShort = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso; }
};
const isExpired = (iso) => {
  if (!iso) return false;
  try { return new Date(iso) < new Date(); } catch { return false; }
};

export default function AdminB2BPlaquette() {
  const [vaultInfo, setVaultInfo] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIframe, setShowIframe] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newDays, setNewDays] = useState('30');
  const [creating, setCreating] = useState(false);

  // After creation we display the one-time token + QR
  const [justCreated, setJustCreated] = useState(null); // { label, raw_token, share_url, expires_at }

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [vi, ts] = await Promise.all([
        api.b2bPlaquetteVaultInfo(),
        api.b2bPlaquetteListTokens(),
      ]);
      setVaultInfo(vi);
      setTokens(ts.items || []);
    } catch (e) {
      toast.error("Impossible de charger la plaquette B2B.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const absoluteShareUrl = (shareUrl) => {
    if (!shareUrl) return '';
    if (shareUrl.startsWith('http')) return shareUrl;
    const base = (vaultInfo?.public_base_url) || (typeof window !== 'undefined' ? window.location.origin : '');
    return `${base}${shareUrl.startsWith('/') ? '' : '/'}${shareUrl}`;
  };

  const copyText = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success('Copié dans le presse-papiers.');
    } catch {
      toast.error('Impossible de copier.');
    }
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (newLabel.trim().length < 2) {
      toast.error('Donnez un nom (ex. "Moto-école Dupont").');
      return;
    }
    setCreating(true);
    try {
      const res = await api.b2bPlaquetteCreateToken(newLabel.trim(), parseInt(newDays, 10) || 30);
      setJustCreated({
        label: res.label,
        raw_token: res.raw_token,
        share_url: absoluteShareUrl(res.share_url),
        expires_at: res.expires_at,
      });
      setCreateOpen(false);
      setNewLabel('');
      setNewDays('30');
      await loadAll();
      toast.success('Code partenaire créé.');
    } catch (e) {
      toast.error(e?.message || 'Erreur à la création.');
    } finally { setCreating(false); }
  };

  const handleRevoke = async (t) => {
    if (!window.confirm(`Révoquer le code "${t.label}" ? Les prospects qui ont ce lien ne pourront plus accéder à la plaquette.`)) return;
    try {
      await api.b2bPlaquetteRevokeToken(t.id);
      toast.success('Code révoqué.');
      await loadAll();
    } catch (e) {
      toast.error(e?.message || 'Erreur à la révocation.');
    }
  };

  const vaultUrl = vaultInfo?.vault_url || '';

  return (
    <div className="space-y-6" data-testid="admin-b2b-plaquette-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-red-600/10 text-red-500 flex items-center justify-center shrink-0">
            <ShieldCheck size={22} />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-black leading-tight text-white">Plaquette B2B</h1>
            <p className="text-sm text-white/55 mt-1 leading-relaxed max-w-xl">
              Partagez votre plaquette commerciale avec des moto-écoles invitées via un code
              d&apos;accès unique + QR code. Les élèves et visiteurs n&apos;y ont pas accès.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowIframe((v) => !v)}
            className="border-white/10 bg-white/5 text-white hover:bg-white/10"
            data-testid="b2b-plaquette-toggle-preview"
          >
            {showIframe ? <EyeOff size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />}
            {showIframe ? 'Masquer' : 'Aperçu'}
          </Button>
          {vaultUrl && (
            <a
              href={vaultUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition"
              data-testid="b2b-plaquette-open-fullscreen"
            >
              <ExternalLink size={14} /> Plein écran
            </a>
          )}
        </div>
      </div>

      {/* Preview iframe */}
      {showIframe && vaultUrl && (
        <Card className="border-white/10 bg-[#0d0d0e] overflow-hidden" data-testid="b2b-plaquette-preview">
          <div className="aspect-[9/16] sm:aspect-[16/10] w-full max-h-[70vh] bg-white">
            <iframe
              src={vaultUrl}
              title="Plaquette B2B"
              className="w-full h-full block"
              loading="lazy"
            />
          </div>
        </Card>
      )}

      {/* Tokens management */}
      <Card className="border-white/10 bg-[#0d0d0e]">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-bold">Codes partenaires</div>
              <div className="text-xs text-white/40 mt-1">Un code = un lien + un QR code à envoyer à un prospect.</div>
            </div>
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
              data-testid="b2b-plaquette-create-btn"
            >
              <Plus size={14} className="mr-1.5" /> Nouveau code
            </Button>
          </div>

          {loading ? (
            <div className="py-10 text-center text-white/40 text-sm flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Chargement…
            </div>
          ) : tokens.length === 0 ? (
            <div className="py-10 text-center text-white/40 text-sm" data-testid="b2b-plaquette-empty">
              Aucun code d&apos;accès pour l&apos;instant. Créez-en un pour envoyer la plaquette à un prospect.
            </div>
          ) : (
            <div className="space-y-2" data-testid="b2b-plaquette-tokens-list">
              {tokens.map((t) => {
                const expired = isExpired(t.expires_at);
                const inactive = t.revoked || expired;
                return (
                  <div
                    key={t.id}
                    className={`rounded-xl border p-3.5 flex items-center gap-3 transition ${
                      inactive ? 'bg-white/[0.02] border-white/5 opacity-60' : 'bg-white/[0.03] border-white/10 hover:border-white/20'
                    }`}
                    data-testid={`b2b-plaquette-token-${t.id}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-red-500/10 text-red-400 flex items-center justify-center shrink-0">
                      <QrCode size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-white text-sm truncate">{t.label || 'Sans nom'}</div>
                        {t.revoked && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 font-bold uppercase tracking-wider">Révoqué</span>}
                        {!t.revoked && expired && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-bold uppercase tracking-wider">Expiré</span>}
                        {!inactive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 font-bold uppercase tracking-wider">Actif</span>}
                      </div>
                      <div className="text-[11px] text-white/40 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="font-mono">{t.token_prefix}…</span>
                        <span className="inline-flex items-center gap-1"><Clock size={10} /> expire {fmtDateShort(t.expires_at)}</span>
                        <span className="inline-flex items-center gap-1"><UsersIcon size={10} /> {t.usage_count || 0} accès</span>
                      </div>
                    </div>
                    {!inactive && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevoke(t)}
                        className="text-red-300 hover:text-red-200 hover:bg-red-500/10"
                        data-testid={`b2b-plaquette-revoke-${t.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-5 pt-4 border-t border-white/5 text-[11px] text-white/35 leading-relaxed">
            <strong className="text-white/55">Sécurité :</strong> le code en clair n&apos;est affiché qu&apos;une seule
            fois à la création. Ensuite seuls les 8 premiers caractères sont visibles. Si vous
            l&apos;avez perdu, révoquez-le et créez-en un nouveau.
          </div>
        </CardContent>
      </Card>

      {/* Modal: create new token */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="b2b-plaquette-create-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau code partenaire</DialogTitle>
            <DialogDescription>
              Donnez un nom au destinataire pour vous y retrouver (ex. nom de la moto-école).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 pt-2">
            <div>
              <Label htmlFor="pt-label">Nom du prospect</Label>
              <Input
                id="pt-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Ex. Moto-école Dupont · Lyon"
                autoFocus
                data-testid="b2b-plaquette-new-label"
              />
            </div>
            <div>
              <Label htmlFor="pt-days">Durée de validité</Label>
              <Select value={newDays} onValueChange={setNewDays}>
                <SelectTrigger id="pt-days" data-testid="b2b-plaquette-new-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 jours</SelectItem>
                  <SelectItem value="30">30 jours</SelectItem>
                  <SelectItem value="90">3 mois</SelectItem>
                  <SelectItem value="180">6 mois</SelectItem>
                  <SelectItem value="365">1 an</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={creating} className="bg-red-600 hover:bg-red-700" data-testid="b2b-plaquette-create-submit">
                {creating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
                Créer et générer le QR
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal: after creation (one-shot display with QR) */}
      <Dialog open={!!justCreated} onOpenChange={(v) => !v && setJustCreated(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="b2b-plaquette-created-dialog">
          {justCreated && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldCheck size={18} className="text-green-500" />
                  Code créé pour « {justCreated.label} »
                </DialogTitle>
                <DialogDescription>
                  Envoyez ce QR code ou le lien à votre prospect. <strong>Ce code en clair n&apos;est
                  affiché qu&apos;une seule fois</strong>, pensez à le sauvegarder ou le partager
                  maintenant. Valable jusqu&apos;au {fmtDate(justCreated.expires_at)}.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 pt-2">
                {/* QR Code */}
                <div className="bg-white rounded-xl p-5 flex items-center justify-center" data-testid="b2b-plaquette-qr">
                  <QRCodeCanvas
                    id="b2b-qr-canvas"
                    value={justCreated.share_url}
                    size={220}
                    level="M"
                    fgColor="#0F172A"
                    bgColor="#FFFFFF"
                    includeMargin={true}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const canvas = document.getElementById('b2b-qr-canvas');
                      if (!canvas) return;
                      const url = canvas.toDataURL('image/png');
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `qr-plaquette-${justCreated.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
                      a.click();
                    }}
                    data-testid="b2b-plaquette-qr-download"
                  >
                    <Download size={14} className="mr-1.5" /> QR en PNG
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => copyText(justCreated.share_url)}
                    data-testid="b2b-plaquette-qr-copy-link"
                  >
                    <Copy size={14} className="mr-1.5" /> Copier le lien
                  </Button>
                </div>

                {/* Share URL */}
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Lien à envoyer
                  </Label>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-muted text-xs font-mono break-all" data-testid="b2b-plaquette-share-url">
                      {justCreated.share_url}
                    </code>
                  </div>
                </div>

                {/* Raw token */}
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Code brut (si le prospect veut le saisir manuellement)
                  </Label>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-muted text-xs font-mono break-all" data-testid="b2b-plaquette-raw-token">
                      {justCreated.raw_token}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => copyText(justCreated.raw_token)}
                      data-testid="b2b-plaquette-raw-copy"
                    >
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>

                {/* Email helper */}
                <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-3">
                  <div className="flex items-start gap-2">
                    <Mail size={14} className="text-blue-400 mt-0.5 shrink-0" />
                    <div className="text-xs text-blue-200/90 leading-relaxed">
                      <strong>Astuce email :</strong> ajoutez le QR en pièce jointe + le lien
                      cliquable dans le corps du message. Les prospects sur mobile scannent le
                      QR, ceux sur ordinateur cliquent sur le lien.
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  onClick={() => setJustCreated(null)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="b2b-plaquette-created-close"
                >
                  J&apos;ai bien sauvegardé le code
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
